import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PauseIcon from '@mui/icons-material/Pause';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UndoIcon from '@mui/icons-material/Undo';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import { colors } from '../../theme/tokens';
import { elapsedSince } from '../../utils/timeAgo';

/** sub_status → human label for in-flight steps. */
const friendlySubStatus = (sub: string | null | undefined): string => {
  switch (sub) {
    case 'sent': return 'dispatching';
    case 'verified': return 'verifying';
    case 'platform_written': return 'writing platform';
    case 'cache_invalidated': return 'finishing';
    default: return 'applying';
  }
};

/**
 * WU12 — Per-environment timeline status taxonomy.
 *
 * Mirrors the per-step status set the server emits (`promotion_workflow_step.status`)
 * plus a couple of synthetic ones the page collapses to before rendering:
 *   - `completed`          → check icon, green
 *   - `partial`            → warning triangle, amber
 *   - `failed`             → error icon, red
 *   - `paused`             → pause icon, neutral
 *   - `pending` / `in_flight` → empty dot or pulse
 *   - `awaiting_approval`  → hourglass icon, purple ring (parked, no pulse)
 *   - `expired`            → clock icon, neutral
 *   - `rolled_back`        → undo icon, neutral
 *   - `unknown`            → ? icon, outlined
 */
export type PipelineStepStatus =
  | 'completed'
  | 'partial'
  | 'failed'
  | 'paused'
  | 'pending'
  | 'in_flight'
  | 'awaiting_approval'
  | 'expired'
  | 'rolled_back'
  | 'unknown';

export interface PipelineStep {
  /** Env label shown beneath the dot (e.g. "DEV01", "QA"). */
  env: string;
  status: PipelineStepStatus;
  /** Optional sublabel (e.g. "12m ago" or "awaiting approval"). */
  sublabel?: string;
  /**
   * For in-flight steps: the raw sub_status from the server.
   * Used to derive a human-friendly "dispatching / verifying / …" label.
   */
  subStatus?: string | null;
  /**
   * ISO timestamp for the start of the current in-flight phase
   * (promoted_at ?? last_seen_status_at). Drives the elapsed counter.
   */
  sinceIso?: string | null;
}

export interface PipelineTimelineProps {
  steps: PipelineStep[];
  /**
   * The home env for this workflow — drawn with a "★" prefix on the label
   * so users can tell which env originated the change.
   */
  homeEnv?: string;
  /**
   * Index of the active (in-flight) step. When set, that dot gets the
   * brand-yellow pulse halo.
   */
  activeStep?: number;
  size?: 'small' | 'medium';
  /** Optional click handler — called with the step index. */
  onStepClick?: (index: number, step: PipelineStep) => void;
  /**
   * WU13 — indices of steps to render with a faded (0.4 opacity) dot +
   * connector. Used by the CreateWorkflowModal ladder preview so envs
   * outside the user-selected scope appear "ghosted" rather than fully
   * styled. Steps at these indices remain accessible but are
   * visually de-emphasized.
   */
  disabledIndices?: number[];
}

interface StatusVisual {
  icon: React.ReactNode | null;
  bg: string;
  border?: string;
}

const visualFor = (status: PipelineStepStatus, iconSize: number): StatusVisual => {
  switch (status) {
    case 'completed':
      return { icon: <CheckCircleIcon sx={{ fontSize: iconSize, color: '#fff' }} />, bg: colors.successIcon };
    case 'partial':
      return { icon: <WarningAmberIcon sx={{ fontSize: iconSize, color: '#fff' }} />, bg: colors.warningIcon };
    case 'failed':
      return { icon: <ErrorOutlineIcon sx={{ fontSize: iconSize, color: '#fff' }} />, bg: colors.errorIcon };
    case 'paused':
      return { icon: <PauseIcon sx={{ fontSize: iconSize, color: '#fff' }} />, bg: colors.textTertiary };
    case 'expired':
      return { icon: <AccessTimeIcon sx={{ fontSize: iconSize, color: '#fff' }} />, bg: colors.textTertiary };
    case 'rolled_back':
      return { icon: <UndoIcon sx={{ fontSize: iconSize, color: '#fff' }} />, bg: colors.textTertiary };
    case 'unknown':
      return {
        icon: <HelpOutlineIcon sx={{ fontSize: iconSize, color: colors.textSecondary }} />,
        bg: colors.bgWhite,
        border: `1px solid ${colors.borderDefault}`,
      };
    case 'awaiting_approval':
      // Parked for four-eyes approval — purple ring, no pulse (it's stationary).
      return {
        icon: <HourglassEmptyIcon sx={{ fontSize: iconSize, color: '#fff' }} />,
        bg: colors.approvalIcon,
        border: `2px solid ${colors.approvalBg}`,
      };
    case 'in_flight':
      return { icon: <Box sx={{ width: iconSize * 0.45, height: iconSize * 0.45, borderRadius: '50%', backgroundColor: '#fff' }} />, bg: colors.brand };
    case 'pending':
    default:
      return { icon: null, bg: colors.bgMuted };
  }
};

const aria = (env: string, status: PipelineStepStatus) =>
  `${env}: ${status.replace(/_/g, ' ')}`;

/**
 * Reusable timeline component for promotion / pipeline visualizations.
 * Extracted from the Phase-A pipeline card grid during WU12 so the card grid
 * and the WU13 detail page share a single implementation.
 *
 * The active (`in_flight`) step gets a pulsing brand-yellow halo (via CSS
 * keyframes). The halo respects `prefers-reduced-motion`. The
 * `awaiting_approval` step renders a static purple ring — parked, not moving.
 */
const PipelineTimeline: React.FC<PipelineTimelineProps> = ({
  steps,
  homeEnv,
  activeStep,
  size = 'medium',
  onStepClick,
  disabledIndices,
}) => {
  const dotSize = size === 'small' ? 20 : 28;
  const iconSize = size === 'small' ? 12 : 16;
  const labelFontSize = size === 'small' ? 10 : 11;
  const minWidth = size === 'small' ? 56 : 72;
  const disabledSet = React.useMemo(
    () => new Set(disabledIndices ?? []),
    [disabledIndices],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.25,
        width: '100%',
        flexWrap: 'nowrap',
      }}
    >
      {steps.map((step, i) => {
        const v = visualFor(step.status, iconSize);
        const isActive = activeStep === i;
        const isHome = homeEnv && homeEnv.toUpperCase() === step.env.toUpperCase();
        const isClickable = !!onStepClick;
        const isDisabled = disabledSet.has(i);

        return (
          <React.Fragment key={`${step.env}-${i}`}>
            <Box
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              aria-label={aria(step.env, step.status)}
              onClick={isClickable ? () => onStepClick!(i, step) : undefined}
              onKeyDown={
                isClickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onStepClick!(i, step);
                      }
                    }
                  : undefined
              }
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                minWidth,
                cursor: isClickable ? 'pointer' : 'default',
                opacity: isDisabled ? 0.4 : 1,
                '&:focus-visible': isClickable
                  ? { outline: `2px solid ${colors.brand}`, outlineOffset: 2, borderRadius: '4px' }
                  : undefined,
              }}
            >
              <Box
                sx={{
                  width: dotSize,
                  height: dotSize,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: v.bg,
                  border: v.border,
                  // Active-step pulse — brand-yellow halo. Drops the
                  // animation when the user prefers reduced motion.
                  ...(isActive && {
                    boxShadow: `0 0 0 4px ${colors.brandLight}`,
                    animation: 'pipelineTimelinePulse 2s infinite',
                    '@keyframes pipelineTimelinePulse': {
                      '0%': { boxShadow: `0 0 0 0 ${colors.brandLight}` },
                      '70%': { boxShadow: '0 0 0 8px rgba(250,179,6,0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(250,179,6,0)' },
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                      boxShadow: `0 0 0 4px ${colors.brandLight}`,
                    },
                  }),
                }}
              >
                {v.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: labelFontSize,
                  fontWeight: 600,
                  color: isActive || step.status === 'completed' ? colors.textPrimary : colors.textTertiary,
                  textAlign: 'center',
                  fontFamily: 'Manrope, sans-serif',
                  letterSpacing: '0.3px',
                }}
              >
                {isHome ? `★ ${step.env}` : step.env}
              </Typography>
              {step.status === 'in_flight' ? (
                <Typography
                  sx={{
                    fontSize: labelFontSize - 1,
                    color: colors.textTertiary,
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {friendlySubStatus(step.subStatus)}
                  {step.sinceIso ? ` · ${elapsedSince(step.sinceIso)}` : ''}
                </Typography>
              ) : step.sublabel ? (
                <Typography
                  sx={{
                    fontSize: labelFontSize - 1,
                    color: colors.textTertiary,
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {step.sublabel}
                </Typography>
              ) : null}
            </Box>
            {i < steps.length - 1 && (
              <Box
                aria-hidden
                sx={{
                  flex: 1,
                  height: 2,
                  alignSelf: 'flex-start',
                  // Center vertically with the dot — dotSize/2 minus the
                  // 1px half-height of the connector.
                  mt: `${dotSize / 2 - 1}px`,
                  backgroundColor:
                    step.status === 'completed' ? colors.successIcon : colors.borderLight,
                  // Fade the connector if either end is disabled — keeps
                  // the ladder visually consistent.
                  opacity: isDisabled || disabledSet.has(i + 1) ? 0.4 : 1,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default PipelineTimeline;
